/// Auto-generated with ScanbotSDKCodegenV3. Modifications will be overwritten.
/// Generated from xplatforms/schemas/document_scanner/Sdk.yaml

import { ScanbotBarcode } from './Barcode';
import { ScanbotCheck } from './Check';
import { ScanbotCreditCard } from './CreditCard';
import { ScanbotDocument } from './Document';
import { ScanbotDocumentDataExtractor } from './DocumentDataExtractor';
import { ScanbotImageProcessor } from './ImageProcessor';
import { ScanbotLegacyPage } from './LegacyPage';
import { ScanbotMrz } from './Mrz';
import { ScanbotOcrEngine } from './OcrEngine';
import { ScanbotPdfGenerator } from './PdfGenerator';
import { ScanbotPdfImageExtractor } from './PdfImageExtractor';
import { ScanbotTextPattern } from './TextPattern';
import { ScanbotTiffGenerator } from './TiffGenerator';
import { ScanbotVin } from './Vin';
import { ScanbotSdkImpl } from './impl';
import type { OcrConfigsResult } from './types/base/OcrEngineResult';
import type { SdkConfiguration } from './types/base/SdkConfiguration';
import type { AnalyticsSubscriber } from './types/core/analytics/AnalyticsSubscriberImpl';
import type { LicenseInfo } from './types/core/licensing/LicensingTypes';

/**
 * Entry point for all features that are part of Scanbot SDK.
 */
export const ScanbotSdk = {
  /**
   * Used for barcode scanning features.
   */
  Barcode: ScanbotBarcode,
  /**
   * Used for check scanning features.
   */
  Check: ScanbotCheck,
  /**
   * Used for credit card scanning features.
   */
  CreditCard: ScanbotCreditCard,
  /**
   * Used for document scanning features.
   */
  Document: ScanbotDocument,
  /**
   * Used for document data extraction features.
   */
  DocumentDataExtractor: ScanbotDocumentDataExtractor,
  /**
   * Used for image processing features.
   */
  ImageProcessor: ScanbotImageProcessor,
  /**
   * Used for legacy page features.
   * @deprecated Please, use ***ScanbotDocument*** instead.
   */
  LegacyPage: ScanbotLegacyPage,
  /**
   * Used for MRZ scanning features.
   */
  Mrz: ScanbotMrz,
  /**
   * Used for OCR features.
   */
  OcrEngine: ScanbotOcrEngine,
  /**
   * Used for PDF generation features.
   */
  PdfGenerator: ScanbotPdfGenerator,
  /**
   * Used to extract images from PDF documents.
   */
  PdfImageExtractor: ScanbotPdfImageExtractor,
  /**
   * Used for text pattern recognition features.
   */
  TextPattern: ScanbotTextPattern,
  /**
   * Used for TIFF generation features.
   */
  TiffGenerator: ScanbotTiffGenerator,
  /**
   * Used for VIN scanning features.
   */
  Vin: ScanbotVin,
  /**
   * Retrieves the OCR configurations available in the Scanbot SDK.
   * @returns {Promise<OcrConfigsResult>} - The OCR configurations available in the Scanbot SDK.
   * @deprecated
   */
  async getOcrConfigs(): Promise<OcrConfigsResult> {
    return ScanbotSdkImpl.getOcrConfigs();
  },
  /**
   * Mock camera preview by using static images as a frame source. This is useful for testing purposes.
   * @param {string} params.imageFileUri - The file uri of the image to be used as a mock camera frame.
   * @param {string} params.captureFileUri - The file uri of the image to be used as a mock captured frame. If not provided, the imageFileUri will be used. Default is ""
   * @param {boolean} params.refreshOnEachFrame - If true, the mock camera will refresh the frame on each request, simulating a live camera feed. Default is false
   * @returns {Promise<void>}
   */
  async mockCamera(params: {
    imageFileUri: string;
    captureFileUri?: string;
    refreshOnEachFrame?: boolean;
  }): Promise<void> {
    return ScanbotSdkImpl.mockCamera(params);
  },
  /**
   * Sets a custom analytics subscriber to receive analytics events from the Scanbot SDK.
   * @param {AnalyticsSubscriber | null} subscriber - The analytics subscriber to be set.
   * @returns {Promise<void>}
   */
  async setAnalyticsSubscriber(subscriber: AnalyticsSubscriber | null): Promise<void> {
    return ScanbotSdkImpl.setAnalyticsSubscriber(subscriber);
  },
  /**
   * Initializes the Scanbot SDK with the preferred configuration.
   * @param {SdkConfiguration} configuration - The SDK configuration to be used for initialization.
   * @returns {Promise<LicenseInfo>} - Returns complete information about the license used for initialization.
   */
  async initialize(configuration: SdkConfiguration): Promise<LicenseInfo> {
    return ScanbotSdkImpl.initialize(configuration);
  },
  /**
   * Provides complete information about the current license.
   * @returns {Promise<LicenseInfo>} - Returns complete information about the current license.
   */
  async getLicenseInfo(): Promise<LicenseInfo> {
    return ScanbotSdkImpl.getLicenseInfo();
  },
  /**
   * Cleans up the storage used by the Scanbot SDK.
   * @returns {Promise<void>}
   */
  async cleanupStorage(): Promise<void> {
    return ScanbotSdkImpl.cleanupStorage();
  },
};
